import React, { useRef, useEffect, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import pricingBackground from '../images/section4back.png';
import { FaArrowRight } from 'react-icons/fa';
import { IoMdArrowDropdownCircle } from 'react-icons/io';

const GlobalBoxSizing = createGlobalStyle`
  *, *::before, *::after {
    box-sizing: border-box;
  }
`;

const Section = styled.section`
  background-image: url(${pricingBackground});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: scroll;
  background-color: #232323;
  box-shadow: inset 0 0 0 1000px rgba(0, 0, 0, 0.4);
  width: 100%;
  min-height: 100vh;
  padding: 80px 20px;
  overflow-x: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 20px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 60px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h2`
  font-size: 3rem;
  margin-bottom: 10px;
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
  @media (min-width: 481px) and (max-width: 767px) {
    font-size: 2.5rem;
    margin-bottom: 12px;
  }
  @media (min-width: 320px) and (max-width: 480px) {
    font-size: 1.9rem;
    margin-bottom: 18px;
    line-height: 1.2;
  }
  @media (max-width: 400px) {
    font-size: 1.7rem;
    margin-bottom: 15px;
  }
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #ccc;
  margin-bottom: 20px;
  max-width: 600px;
  line-height: 1.6;
  text-align: center;
  @media (max-width: 768px) {
    font-size: 14px;
    max-width: 90%;
    margin-bottom: 30px;
  }
  @media (min-width: 481px) and (max-width: 767px) {
    font-size: 14px;
    max-width: 85%;
    margin-bottom: 25px;
    line-height: 1.55;
  }
  @media (min-width: 320px) and (max-width: 480px) {
    font-size: 14px;
    max-width: 92%;
    margin-bottom: 22px;
    line-height: 1.5;
    padding: 0 10px;
  }
  @media (max-width: 400px) {
    font-size: 14px;
    max-width: 95%;
    margin-bottom: 20px;
    padding: 0 5px;
  }
`;

const DropdownWrapper = styled.div`
  position: relative;
  width: 320px;
  margin: 0 auto 0px auto;
  max-width: 100%;
  @media (max-width: 768px) {
    width: 90%;
    margin-bottom: 20px;
  }
  @media (min-width: 481px) and (max-width: 767px) {
    width: 85%;
    margin-bottom: 18px;
  }
  @media (min-width: 320px) and (max-width: 480px) {
    width: 100%;
    margin-bottom: 16px;
  }
  @media (max-width: 400px) {
    width: 95%;
    margin-bottom: 15px;
  }
`;

const Dropdown = styled.select`
  width: 100%;
  padding: 14px 20px;
  padding-right: 45px;
  font-size: 1.05rem;
  font-weight: 500;
  border: 2px solid transparent;
  border-radius: 16px;
  appearance: none;
  background-color: #fff;
  background-image: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
  color: #333;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(0,0,0,0.12);
  transition: all 0.3s ease;
  text-overflow: ellipsis;
  
  &:hover {
    box-shadow: 0 8px 25px rgba(0,0,0,0.18);
    transform: translateY(-1px);
    border-color: rgba(84, 183, 61, 0.2);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(84, 183, 61, 0.3), 0 8px 25px rgba(0,0,0,0.15);
    border-color: #54B73D;
    transform: translateY(-1px);
  }
  
  option {
    background: #fff;
    color: #333;
    font-size: 1rem;
    padding: 12px 16px;
    font-weight: 500;
    border: none;
    font-family: inherit;
    text-overflow: ellipsis;
    white-space: normal;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    padding: 12px 16px;
    padding-right: 35px;
  }
`;

const DropdownIcon = styled.span`
  position: absolute;
  top: 50%;
  right: 18px;
  transform: translateY(-50%);
  pointer-events: none;
  transition: all 0.3s ease;
  
  &::after {
    content: '▼';
    font-size: 0.9rem;
    color: #54B73D;
    font-weight: bold;
  }
  
  ${Dropdown}:focus ~ & {
    transform: translateY(-50%) rotate(180deg);
  }
  
  @media (max-width: 480px) {
    right: 12px;
    &::after {
      font-size: 0.8rem;
    }
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 35px;
  width: 100%;
  max-width: 1350px;
  box-sizing: border-box;
  justify-items: center;
  @media (max-width: 1400px) {
    gap: 30px;
    max-width: 1200px;
  }
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 40px;
    max-width: 800px;
  }
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
    max-width: 700px;
  }
  @media (min-width: 481px) and (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 25px;
    padding: 0 20px;
    max-width: 500px;
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  @media (min-width: 320px) and (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 18px;
    padding: 0 12px;
    max-width: 100%;
  }
  @media (max-width: 400px) {
    gap: 15px;
    padding: 0 8px;
  }
`;

const Card = styled.div`
  background: ${({ variant }) =>
    variant === 'week'
      ? 'linear-gradient(145deg, #1f1f1f 60%, #292929 100%)'
      : 'linear-gradient(145deg, #2f2f2f 60%, #3a3a3a 100%)'};
  border-radius: 24px;
  padding: 36px 32px;
  flex: 1 1 300px;
  min-width: 280px;
  max-width: 320px;
  min-height: 500px;
  box-shadow: 0 12px 40px 0 rgba(0,0,0,0.35);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  
  &:hover {
    transform: translateY(-12px);
    box-shadow: 0 20px 60px 0 rgba(0,0,0,0.5);
  }
  
  .savings-badge {
    position: absolute;
    top: -8px;
    right: -8px;
    background: #54B73D;
    color: white;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(84, 183, 61, 0.3);
    z-index: 1;
  }
  
  @media (max-width: 1400px) {
    min-width: 260px;
    max-width: 300px;
    padding: 32px 28px;
  }
  
  @media (max-width: 1200px) {
    max-width: 380px;
    width: 100%;
    min-height: 480px;
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 28px 24px;
    min-height: 450px;
  }
  
  @media (min-width: 481px) and (max-width: 767px) {
    max-width: 450px;
    width: 100%;
    min-height: 420px;
    padding: 30px 26px;
    margin: 0 auto;
    border-radius: 22px;
  }
  
  @media (min-width: 320px) and (max-width: 480px) {
    max-width: 100%;
    width: 100%;
    min-height: 360px;
    padding: 22px 18px;
    margin: 0 auto;
    border-radius: 18px;
  }
  
  @media (max-width: 400px) {
    min-height: 340px;
    padding: 20px 16px;
    border-radius: 16px;
  }
`;

const Centered = styled.div`
  width: 100%;
`;

const PeriodHeader = styled.div`
  margin-bottom: 18px;
  background: #504c4cff;
  border-radius: 14px;
  padding: 10px 20px;
  display: inline-block;
  h3 {
    font-size: 1.7rem;
    color: white;
    margin: 0;
    font-weight: 600;
    @media (min-width: 481px) and (max-width: 767px) {
      font-size: 1.5rem;
    }
    @media (min-width: 320px) and (max-width: 480px) {
      font-size: 1.3rem;
    }
    @media (max-width: 400px) {
      font-size: 1.2rem;
    }
  }
`;

const Description = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 16px;
  color: #d1d1d1;
  padding: 0 8px;
  @media (min-width: 481px) and (max-width: 767px) {
    font-size: 0.9rem;
    margin-bottom: 14px;
    line-height: 1.55;
  }
  @media (min-width: 320px) and (max-width: 480px) {
    font-size: 0.8rem;
    margin-bottom: 11px;
    line-height: 1.4;
  }
  @media (max-width: 400px) {
    font-size: 0.75rem;
    margin-bottom: 10px;
  }
`;

const PriceLine = styled.hr`
  width: 50px;
  border: 1.5px solid #54B73D;
  margin: 24px auto;
`;

const Price = styled.div`
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  .current-price {
    font-size: 2.2rem;
    font-weight: 700;
    color: #54B73D;
    margin: 0;
    animation: priceHighlight 0.5s ease;
  }
  
  .original-price {
    font-size: 1.4rem;
    font-weight: 500;
    color: #888;
    text-decoration: line-through;
    margin: 0;
    position: relative;
  }
  
  .single-price {
    font-size: 2.2rem;
    font-weight: 700;
    color: #fff;
    margin: 0;
    transition: color 0.3s ease;
  }
  
  @keyframes priceHighlight {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
  
  @media (min-width: 481px) and (max-width: 767px) {
    .current-price {
      font-size: 1.9rem;
    }
    .original-price {
      font-size: 1.2rem;
    }
    .single-price {
      font-size: 1.9rem;
    }
  }
  
  @media (min-width: 320px) and (max-width: 480px) {
    .current-price {
      font-size: 1.6rem;
    }
    .original-price {
      font-size: 1rem;
    }
    .single-price {
      font-size: 1.6rem;
    }
  }
  
  @media (max-width: 400px) {
    .current-price {
      font-size: 1.5rem;
    }
    .original-price {
      font-size: 0.9rem;
    }
    .single-price {
      font-size: 1.5rem;
    }
  }
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 30px 0;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const FeatureItem = styled.li`
  font-size: 1rem;
  color: #e0e0e0;
  margin-bottom: 10px;
  display: flex;
  align-items: flex-start;
  text-align: left;
  justify-content: flex-start;
  .checkmark {
    font-size: 1.1rem;
    margin-right: 12px;
    color: #54B73D;
    flex-shrink: 0;
    margin-top: 1px;
  }
  @media (min-width: 481px) and (max-width: 767px) {
    font-size: 0.9rem;
    margin-bottom: 7px;
    .checkmark {
      font-size: 0.95rem;
      margin-right: 9px;
    }
  }
  @media (min-width: 320px) and (max-width: 480px) {
    font-size: 0.8rem;
    margin-bottom: 6px;
    .checkmark {
      font-size: 0.85rem;
      margin-right: 7px;
    }
  }
  @media (max-width: 400px) {
    font-size: 0.75rem;
    margin-bottom: 5px;
    .checkmark {
      font-size: 0.8rem;
      margin-right: 6px;
    }
  }
`;

const JoinButton = styled.button`
  background: #ffffff;
  color: #232323;
  border: none;
  border-radius: 50px;
  padding: 14px 0;
  font-size: 1.15rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 45px;
  width: 92%;
  margin: 0 auto;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
  
  &:hover {
    background: #54B73D;
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(84, 183, 61, 0.3);
  }
  
  @media (min-width: 481px) and (max-width: 767px) {
    font-size: 1.05rem;
    padding: 11px 0;
    gap: 36px;
    width: 92%;
  }
  @media (min-width: 320px) and (max-width: 480px) {
    font-size: 0.95rem;
    padding: 10px 0;
    gap: 24px;
    width: 96%;
  }
  @media (max-width: 400px) {
    font-size: 0.9rem;
    padding: 9px 0;
    gap: 20px;
    width: 98%;
  }
`;

const JoinText = styled.span``;

const ArrowCircle = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  background: #54B73D;
  border-radius: 50%;
  width: 34px;
  height: 34px;
  transition: background 0.2s;
  svg {
    color: #fff;
    font-size: 1.3rem;
  }
  ${JoinButton}:hover & {
    background: #ffffff;
    svg {
      color: #54B73D;
    }
  }
`;

function PricingSection({ scrollToContact, onBranchSelect }) {
  // Branch locations data with pricing
  const branches = [
    "Stonehousepet, Nellore",
    "Harinathpuram, Nellore",
    "Vanamthopu Center, Nellore",
    "Current Office Center, Nellore",
    "Vedayapalem, Nellore",
    "BV Nagar, Nellore",
    "Dhanalakshmi, Nellore"
  ];

  // Pricing data for each branch
  const branchPricing = {
    "Harinathpuram, Nellore": {
      "1 Month": { current: 3000, original: null },
      "3 Months": { current: 6000, original: 7000 },
      "6 Months": { current: 9000, original: 10000 },
      "1 Year": { current: 13000, original: 18000 }
    },
    "Stonehousepet, Nellore": {
      "1 Month": { current: 3000, original: null },
      "3 Months": { current: 6000, original: 7000 },
      "6 Months": { current: 9000, original: 10000 },
      "1 Year": { current: 13000, original: 18000 }
    },
    "Vedayapalem, Nellore": {
      "1 Month": { current: 3000, original: null },
      "3 Months": { current: 6000, original: 7000 },
      "6 Months": { current: 9000, original: 10000 },
      "1 Year": { current: 13000, original: 18000 }
    },
    "Current Office Center, Nellore": {
      "1 Month": { current: 2500, original: null },
      "3 Months": { current: 5500, original: 6000 },
      "6 Months": { current: 8000, original: 9000 },
      "1 Year": { current: 11000, original: 16000 }
    },
    "Vanamthopu Center, Nellore": {
      "1 Month": { current: 3500, original: null },
      "3 Months": { current: 7500, original: 9000 },
      "6 Months": { current: 10000, original: 14000 },
      "1 Year": { current: 14000, original: 20000 }
    },
    "BV Nagar, Nellore": {
      "1 Month": { current: 3000, original: null },
      "3 Months": { current: 7000, original: 8000 },
      "6 Months": { current: 10000, original: 12000 },
      "1 Year": { current: 13000, original: 18000 }
    },
    "Dhanalakshmi, Nellore": {
      "1 Month": { current: 2000, original: null },
      "3 Months": { current: 5000, original: 7000 },
      "6 Months": { current: 7500, original: 9000 },
      "1 Year": { current: 10000, original: 14000 }
    }
  };

  const [selectedBranch, setSelectedBranch] = useState("Stonehousepet, Nellore");

  // Handle branch selection
  const handleBranchChange = (e) => {
    const branch = e.target.value;
    setSelectedBranch(branch);
    if (onBranchSelect) {
      onBranchSelect(branch);
    }
  };

  // Get pricing for selected branch
  const getCurrentPricing = (period) => {
    return branchPricing[selectedBranch][period] || branchPricing["Stonehousepet, Nellore"][period];
  };

  const pricingPlans = [
    {
      id: 1,
      period: '1 Month',
      variant: 'month',
      description: 'Perfect for trying out the gym or short-term goals.',
      features: [
        'Unlimited gym access for 30 days',
        'Free fitness assessment',
        'Access to all group classes',
        'No joining or cancellation fee',
      ],
    },
    {
      id: 2,
      period: '3 Months',
      variant: 'month',
      description: 'Great for building consistent habits and seeing progress.',
      features: [
        'Unlimited gym access for 90 days',
        'Free fitness assessment',
        'Access to all group classes',
        '1 guest pass',
        'Basic nutrition consultation',
      ],
    },
    {
      id: 3,
      period: '6 Months',
      variant: 'week',
      description: 'Best for steady progress and visible results.',
      features: [
        '6-month full access to all equipment',
        'Free nutrition guidance',
        '2 guest passes',
        '1 personal training session included',
      ],
    },
    {
      id: 4,
      period: '1 Year',
      variant: 'month',
      description: 'Invest in your health long-term — maximum value!',
      features: [
        'Unlimited yearly gym access',
        '4 guest passes',
        'Monthly progress tracking',
        'Free access to special events & workshops',
      ],
    },
  ];

  return (
    <>
      <GlobalBoxSizing />
      <Section id="pricingSection">
        <ContentWrapper>
          <Header>
            <Title>Membership Pricing</Title>
            <Subtitle>We offer flexible membership plans with premium features, expert support, and great value for individuals and teams alike. {!selectedBranch && <span style={{ color: '#54B73D', fontWeight: '600' }}>Please select a branch to see pricing.</span>}</Subtitle>
            <DropdownWrapper>
              <Dropdown value={selectedBranch} onChange={handleBranchChange}>
                {branches.map((branch, index) => (
                  <option key={index} value={branch}>
                    {branch}
                  </option>
                ))}
              </Dropdown>
              <DropdownIcon />
            </DropdownWrapper>
          </Header>

          <CardsContainer>
            {pricingPlans.map((plan) => {
              const pricing = getCurrentPricing(plan.period);
              const savings = pricing.original ? pricing.original - pricing.current : 0;

              return (
                <Card key={plan.id} variant={plan.variant}>
                  {savings > 0 && (
                    <div className="savings-badge">
                      Save ₹{savings.toLocaleString('en-IN')}
                    </div>
                  )}
                  <Centered>
                    <PeriodHeader>
                      <h3>{plan.period}</h3>
                    </PeriodHeader>
                    <Description>{plan.description}</Description>
                    <PriceLine />
                    <Price>
                      {pricing.original ? (
                        <>
                          <div className="original-price">₹{pricing.original.toLocaleString('en-IN')}</div>
                          <div className="current-price">₹{pricing.current.toLocaleString('en-IN')}</div>
                        </>
                      ) : (
                        <div className="single-price">₹{pricing.current.toLocaleString('en-IN')}</div>
                      )}
                    </Price>
                    <FeaturesList>
                      {plan.features.map((feature, index) => (
                        <FeatureItem key={index}>
                          <span className="checkmark">✔</span> {feature}
                        </FeatureItem>
                      ))}
                    </FeaturesList>
                    <JoinButton onClick={scrollToContact}>
                      <JoinText>Join Now</JoinText>
                      <ArrowCircle><FaArrowRight /></ArrowCircle>
                    </JoinButton>
                  </Centered>
                </Card>
              );
            })}
          </CardsContainer>
        </ContentWrapper>
      </Section>
    </>
  );
}

export default PricingSection;
